import { useState } from 'react';
import { Link } from 'react-router';
import { useApi, useConsulta } from '@plataforma/web/api';
import { useSesion } from '@plataforma/web/sesion';
import { ETAPAS } from './Taller';

interface OrdenTablero {
  ordenId: string;
  vehiculoId: string;
  vin: string;
  tipo: 'reparacion' | 'preparacion';
  estado: string;
  diasEnTaller: number;
  avancePct: number;
}
interface Tarea {
  id: string;
  etapa: string;
  descripcion: string;
  responsable: string | null;
  horasEstimadas: string;
  horasReales: string;
  estado: 'pendiente' | 'en_proceso' | 'terminada';
  relojCorriendo: boolean;
}
interface Orden {
  id: string;
  vin: string;
  tipo: 'reparacion' | 'preparacion';
  estado: string;
  tareas: Tarea[];
}

const ESTADO_TAREA: Record<Tarea['estado'], [string, string]> = {
  pendiente: ['Pendiente', ''],
  en_proceso: ['En proceso', 'aviso'],
  terminada: ['Terminada', 'exito'],
};

/**
 * Mis tareas (arquitectura 2.15, rol mecánico): las órdenes abiertas del taller y, de cada una,
 * las tareas con reloj para iniciar, detener y terminar desde el celular. Sin montos: el mecánico
 * ve horas, no costos. Primero las tareas a su nombre; después las sin responsable.
 */
export function MisTareas() {
  const api = useApi();
  const { usuario } = useSesion();
  const tablero = useConsulta<OrdenTablero[]>('/workshop/tablero');
  const [ordenId, setOrdenId] = useState<string | null>(null);
  const orden = useConsulta<Orden>(ordenId ? `/workshop/ordenes/${ordenId}` : null);
  const [error, setError] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState<string | null>(null);
  const abiertas = (tablero.datos ?? []).filter(
    (o) => !['cerrada', 'cancelada'].includes(o.estado),
  );
  const nombre = usuario?.nombre ?? '';
  const mias = (t: Tarea) =>
    !t.responsable || (nombre && t.responsable.toLowerCase().includes(nombre.toLowerCase()));

  const accion = async (tarea: Tarea, verbo: 'iniciar' | 'detener' | 'terminar') => {
    setError(null);
    setOcupado(tarea.id);
    try {
      await api('POST', `/workshop/tareas/${tarea.id}/${verbo}`);
      orden.recargar();
      tablero.recargar();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOcupado(null);
    }
  };

  const tareas = (orden.datos?.tareas ?? [])
    .filter((t) => t.estado !== 'terminada')
    .sort((a, b) => Number(mias(b)) - Number(mias(a)));
  return (
    <>
      <div className="encabezado">
        <div>
          <div className="subtitulo">
            <Link to="/taller">Taller</Link> › mis tareas
          </div>
          <h1>Mis tareas</h1>
          <div className="subtitulo">
            Elige un auto, inicia el reloj de la tarea y termínala cuando acabes
          </div>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {tablero.error && <p className="error">{tablero.error}</p>}
      <section className="seccion">
        <header>
          <h2>Autos en taller</h2>
        </header>
        <div className="tarjetas">
          {abiertas.map((o) => (
            <button
              key={o.ordenId}
              className={`tarjeta ${o.ordenId === ordenId ? 'activa' : ''}`}
              style={{ textAlign: 'left', cursor: 'pointer' }}
              onClick={() => setOrdenId(o.ordenId)}
            >
              <div className="rotulo">{o.tipo === 'reparacion' ? 'Reparación' : 'Preparación'}</div>
              <div className="vin">{o.vin}</div>
              <div className="nota">
                {o.avancePct}% · {o.diasEnTaller} días en taller
              </div>
            </button>
          ))}
        </div>
        {abiertas.length === 0 && !tablero.cargando && (
          <p className="vacio">No hay autos abiertos en el taller.</p>
        )}
      </section>
      {ordenId && (
        <section className="seccion">
          <header>
            <h2>Tareas de {orden.datos?.vin ?? '…'}</h2>
          </header>
          {orden.error && <p className="error">{orden.error}</p>}
          <table>
            <thead>
              <tr>
                <th>Etapa</th>
                <th>Tarea</th>
                <th>Responsable</th>
                <th>Horas</th>
                <th>Estado</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tareas.map((t) => {
                const [texto, clase] = ESTADO_TAREA[t.estado];
                return (
                  <tr key={t.id} style={{ opacity: mias(t) ? 1 : 0.6 }}>
                    <td>{ETAPAS[t.etapa] ?? t.etapa}</td>
                    <td>{t.descripcion}</td>
                    <td>{t.responsable ?? <span className="subtitulo">sin asignar</span>}</td>
                    <td className="num">
                      {t.horasReales} / {t.horasEstimadas} h
                    </td>
                    <td>
                      <span className={`etiqueta ${clase}`}>
                        {texto}
                        {t.relojCorriendo ? ' · reloj corriendo' : ''}
                      </span>
                    </td>
                    <td className="num">
                      <div className="acciones">
                        {!t.relojCorriendo && (
                          <button
                            className="boton primario"
                            disabled={ocupado === t.id}
                            onClick={() => void accion(t, 'iniciar')}
                          >
                            Iniciar
                          </button>
                        )}
                        {t.relojCorriendo && (
                          <button
                            className="boton"
                            disabled={ocupado === t.id}
                            onClick={() => void accion(t, 'detener')}
                          >
                            Detener
                          </button>
                        )}
                        <button
                          className="boton"
                          disabled={ocupado === t.id}
                          onClick={() => void accion(t, 'terminar')}
                        >
                          Terminar
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {orden.datos && tareas.length === 0 && (
            <p className="vacio">Todas las tareas de este auto están terminadas.</p>
          )}
        </section>
      )}
    </>
  );
}
